import {
  LauncherSidebarDivider,
  placeSidebarDivider,
  releaseSidebarDivider,
  sidebarCollapseWidth,
} from '@affine/core/desktop/pages/workspace/_shared/launcher-sidebar-divider';
import * as launcherStyles from '@affine/core/desktop/pages/workspace/_shared/launcher-sidebar-layout.css';
import { trackPaneWidthDrag } from '@affine/core/desktop/pages/workspace/_shared/pane-width-drag';
import { useSemanticDomRegion } from '@affine/core/modules/streamlain/copilot/semantic-dom-region';
import { useAreaSidebar } from '@affine/core/modules/streamlain/native-app/area-sidebar';
import {
  createWorkspacePanePaneWidthsStorageKey,
  useWorkspacePanePrimaryPaneController,
} from '@affine/core/modules/streamlain/workspace-pane';
import {
  WORKSPACE_PANE_DEFAULT_PRIMARY_PANE_WIDTH,
  WORKSPACE_PANE_MAX_PRIMARY_PANE_WIDTH,
  WORKSPACE_PANE_MIN_PRIMARY_PANE_WIDTH,
} from '@affine/core/modules/streamlain/workspace-pane/workspace-pane';
import type { View } from '@affine/core/modules/workbench/entities/view';
import {
  setWorkbenchBodyNavigationInset,
  workbenchBodyContent,
} from '@affine/core/modules/workbench/view/body-navigation-inset';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import {
  streamLainAnimationController,
  type StreamLainAnimationHandle,
} from '@blocksuite/affine-shared/utils';
import { FrameworkScope, useLiveData, useService } from '@toeverything/infra';
import {
  type CSSProperties,
  type PointerEventHandler,
  type ReactNode,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';

import { DocumentsPaneSurface } from './documents-pane';
import * as styles from './workspace-document-navigation.css';
import { useWorkspaceNavigationHost } from './workspace-navigation-host';

/** Slides the passing sheet of a narrow window in and out. */
function useSlidePane(ref: RefObject<HTMLDivElement | null>, open: boolean) {
  const previous = useRef<boolean | undefined>(undefined);
  const motion = useRef<ReturnType<
    typeof streamLainAnimationController.play
  > | null>(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const before = previous.current;
    previous.current = open;
    if (before === undefined) {
      node.hidden = !open;
      return;
    }
    if (before === open) return;
    const hidden = { opacity: 0, transform: 'translateX(-100%)' };
    const resting = { opacity: 1, transform: 'translateX(0)' };
    const current = node.ownerDocument.defaultView?.getComputedStyle(node);
    const from =
      motion.current && current
        ? { opacity: Number(current.opacity), transform: current.transform }
        : before
          ? resting
          : hidden;
    motion.current?.cancel();
    node.hidden = false;
    const handle = streamLainAnimationController.play(
      node,
      [from, open ? resting : hidden],
      open ? 'enter' : 'exit'
    );
    motion.current = handle;
    void handle.finished.then(outcome => {
      if (motion.current !== handle) return;
      motion.current = null;
      if (outcome === 'finished') node.hidden = !open;
    });
  }, [open, ref]);

  useLayoutEffect(
    () => () => {
      motion.current?.cancel();
      motion.current = null;
    },
    []
  );
}

/**
 * Where the sidebar sits at the view: in /all's own layout, as a passing sheet on a narrow
 * window, or beside the view, which makes room for it.
 */
type Placement = 'all' | 'sheet' | 'beside';

const CONTENT_WIDTH = '--workspace-navigation-content-width';

/** The rail-owned workspace sidebar, clipped to the active view's actual content rectangle. */
export function WorkspaceDocumentNavigation({
  children,
  open,
  allPageActive,
  narrow,
  view,
  onOpenChange,
  onDocumentInteract,
}: {
  children: ReactNode;
  open: boolean;
  allPageActive: boolean;
  /** The window is too narrow for a sidebar that stays. */
  narrow: boolean;
  view: View;
  onOpenChange: (open: boolean) => void;
  onDocumentInteract: () => void;
}) {
  const host = useWorkspaceNavigationHost(view.id);
  return host
    ? createPortal(
        <FrameworkScope scope={view.scope}>
          <NavigationLayer
            open={open}
            allPageActive={allPageActive}
            narrow={narrow}
            view={view}
            onOpenChange={onOpenChange}
            onDocumentInteract={onDocumentInteract}
          >
            {children}
          </NavigationLayer>
        </FrameworkScope>,
        host
      )
    : null;
}

function NavigationLayer({
  children,
  open,
  allPageActive,
  narrow,
  view,
  onOpenChange,
  onDocumentInteract,
}: {
  children: ReactNode;
  open: boolean;
  allPageActive: boolean;
  narrow: boolean;
  view: View;
  onOpenChange: (open: boolean) => void;
  onDocumentInteract: () => void;
}) {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const widthKey = useMemo(
    () =>
      createWorkspacePanePaneWidthsStorageKey({
        appId: 'workspace-documents',
        workspaceId: workspace.id,
      }),
    [workspace.id]
  );
  const pane = useWorkspacePanePrimaryPaneController(widthKey);
  const width = pane.primaryPaneWidth;
  const persistWidth = pane.setPrimaryPaneWidth;
  const placement: Placement = allPageActive
    ? 'all'
    : narrow
      ? 'sheet'
      : 'beside';
  const beside = placement === 'beside';
  // /all presents the sidebar of its own layout.
  useAreaSidebar(open, onOpenChange, !allPageActive);
  // A page wraps its text anew at every width, so it takes the slide as one change. Any other
  // view follows the sidebar's edge frame by frame, a canvas included: it keeps its top left
  // corner, so what it shows moves along with the edge.
  const follows = useLiveData(view.layoutTransition$) !== 'settled';
  const rootRef = useRef<HTMLDivElement | null>(null);
  // The sidebar stands at the view, outside its body: the assistant reads and operates what it
  // lists as one more region of that view.
  const region = useSemanticDomRegion('navigation-dom', view.id);
  const bindRegion = region.ref;
  const layerRef = useCallback(
    (layer: HTMLDivElement | null) => {
      rootRef.current = layer;
      bindRegion(layer);
    },
    [bindRegion]
  );
  const sidebarRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const dividerRef = useRef<HTMLDivElement>(null);
  useSlidePane(sidebarRef, placement !== 'sheet' || open);

  const motion = useRef<StreamLainAnimationHandle | null>(null);
  const shown = useRef({ boundary: 0, opacity: 0 });
  const previous = useRef<{
    open: boolean;
    placement: Placement;
    width: number;
  } | null>(null);
  const cancelResize = useRef<(() => void) | null>(null);
  // A view that draws by its size keeps one size from the first frame of a slide or drag to the
  // last, and is moved as it is meanwhile.
  const sizeHold = useRef<(() => void) | null>(null);
  const releaseSize = useCallback(() => {
    sizeHold.current?.();
    sizeHold.current = null;
  }, []);
  // The width a running drag asks for; the sidebar may be collapsed meanwhile.
  const dragWidth = useRef<number | null>(null);
  const latestWidth = useRef(width);
  latestWidth.current = width;
  const endMotion = useCallback(() => {
    motion.current?.cancel();
    motion.current = null;
    rootRef.current?.style.removeProperty('grid-template-columns');
    releaseSidebarDivider(dividerRef.current);
    surfaceRef.current?.style.removeProperty('opacity');
  }, []);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const host = root?.parentElement;
    const before = previous.current;
    previous.current = { open, placement, width };
    if (!root || !host) return;
    const content = workbenchBodyContent(host);
    // A drag outlives the sidebar it collapses: pulled back out, it opens it again.
    if (!beside || before?.width !== width) cancelResize.current?.();
    if (!beside) {
      if (before?.placement === 'beside') {
        endMotion();
        root.style.removeProperty(CONTENT_WIDTH);
      }
      setWorkbenchBodyNavigationInset(content, null);
      releaseSize();
      return;
    }
    // The width the open sidebar has right now, a running drag included.
    const openWidth = () =>
      Math.min(
        host.clientWidth * 0.88,
        dragWidth.current ?? latestWidth.current
      );
    const rest = () => {
      endMotion();
      // Collapsed, the content keeps its open width: it neither wraps on the way nor afterwards.
      if (open) root.style.removeProperty(CONTENT_WIDTH);
      else root.style.setProperty(CONTENT_WIDTH, `${openWidth()}px`);
      setWorkbenchBodyNavigationInset(content, open ? openWidth() : null);
      // A drag under way moves the view further.
      if (!cancelResize.current) releaseSize();
    };
    if (!before || before.placement !== placement) {
      rest();
      return;
    }
    if (before.open === open) {
      // A slide under way arrives at the new width by itself.
      if (motion.current)
        root.style.setProperty(CONTENT_WIDTH, `${openWidth()}px`);
      else rest();
      return;
    }
    // One progress value drives the sidebar's boundary, its divider, its content and the room the
    // view leaves for it, including reversals.
    const from = motion.current
      ? shown.current
      : {
          boundary: before.open ? openWidth() : 0,
          opacity: before.open ? 1 : 0,
        };
    const toOpacity = open ? 1 : 0;
    motion.current?.cancel();
    // The room the view has without the sidebar is the most the slide shows of it.
    if (follows) sizeHold.current ??= view.holdSize(from.boundary);
    root.style.setProperty(CONTENT_WIDTH, `${openWidth()}px`);
    // A page makes room once the sidebar has arrived, and takes the room back before it leaves.
    if (!follows && !open) setWorkbenchBodyNavigationInset(content, null);
    const update = (progress: number) => {
      // A drag that opens the sidebar keeps moving its boundary while it arrives.
      const to = open ? openWidth() : 0;
      const boundary = from.boundary + (to - from.boundary) * progress;
      const opacity = from.opacity + (toOpacity - from.opacity) * progress;
      shown.current = { boundary, opacity };
      root.style.gridTemplateColumns = `${boundary}px minmax(0, 1fr)`;
      placeSidebarDivider(dividerRef.current, boundary);
      if (surfaceRef.current) surfaceRef.current.style.opacity = `${opacity}`;
      if (follows) setWorkbenchBodyNavigationInset(content, boundary);
    };
    update(0);
    const handle = streamLainAnimationController.animateProgress(root, update);
    motion.current = handle;
    void handle.finished.then(() => {
      if (motion.current === handle) rest();
    });
  }, [beside, endMotion, follows, open, placement, releaseSize, view, width]);

  useLayoutEffect(() => {
    const host = rootRef.current?.parentElement;
    return () => {
      cancelResize.current?.();
      motion.current?.cancel();
      motion.current = null;
      if (host)
        setWorkbenchBodyNavigationInset(workbenchBodyContent(host), null);
      releaseSize();
    };
  }, [releaseSize]);

  const startResize = useCallback<PointerEventHandler<HTMLDivElement>>(
    event => {
      const root = rootRef.current;
      const host = root?.parentElement;
      const divider = dividerRef.current;
      if (!root || !host || !divider || event.button !== 0) return;
      event.preventDefault();
      cancelResize.current?.();
      const wasOpen = previous.current?.open ?? false;
      // An interrupted slide stands at its end from here on.
      const displayed = parseFloat(getComputedStyle(divider).left);
      endMotion();
      const startWidth = !wasOpen
        ? 0
        : Number.isFinite(displayed)
          ? displayed
          : latestWidth.current;
      const content = workbenchBodyContent(host);
      // The same variable sizes the sidebar and places its divider; the view beside it follows.
      const show = (next: number) => {
        dragWidth.current = next;
        root.style.setProperty('--launcher-pane-width', `${next}px`);
        if (root.style.getPropertyValue(CONTENT_WIDTH))
          root.style.setProperty(CONTENT_WIDTH, `${next}px`);
        if (previous.current?.open && !motion.current)
          setWorkbenchBodyNavigationInset(content, next);
      };
      sizeHold.current ??= view.holdSize(startWidth);
      if (wasOpen) {
        root.style.removeProperty(CONTENT_WIDTH);
        show(startWidth);
      }
      cancelResize.current = trackPaneWidthDrag(event, {
        startWidth,
        minWidth: WORKSPACE_PANE_MIN_PRIMARY_PANE_WIDTH,
        maxWidth: WORKSPACE_PANE_MAX_PRIMARY_PANE_WIDTH,
        collapseBelow: sidebarCollapseWidth(
          WORKSPACE_PANE_MIN_PRIMARY_PANE_WIDTH
        ),
        startCollapsed: !wasOpen,
        onWidth: show,
        onCollapse: collapsed => onOpenChange(!collapsed),
        onEnd: (next, committed, collapsed) => {
          cancelResize.current = null;
          const stored = latestWidth.current;
          if (committed && !collapsed && next !== stored) {
            // The stored width takes over from here; a slide under way arrives at it.
            dragWidth.current = null;
            latestWidth.current = next;
            persistWidth(next);
          } else {
            show(stored);
            dragWidth.current = null;
          }
          // A slide the drag started arrives first.
          if (!motion.current) releaseSize();
        },
      });
    },
    [endMotion, onOpenChange, persistWidth, releaseSize, view]
  );
  const resetWidth = useCallback(() => {
    if (latestWidth.current !== WORKSPACE_PANE_DEFAULT_PRIMARY_PANE_WIDTH)
      persistWidth(WORKSPACE_PANE_DEFAULT_PRIMARY_PANE_WIDTH);
  }, [persistWidth]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const sidebar = sidebarRef.current;
    const viewport = root?.parentElement;
    if (!allPageActive || !root || !sidebar || !viewport) return;
    // Read the existing controller's geometry instead of running a second
    // sidebar animation. ResizeObserver runs before paint, including dragging.
    let resize: ResizeObserver | undefined;
    let geometry: MutationObserver | undefined;
    const attach = () => {
      const slot = viewport.querySelector<HTMLElement>(
        '[data-workspace-sidebar-slot]'
      );
      const content = slot?.parentElement;
      const source = content?.parentElement;
      const layout = source?.parentElement;
      if (!source || !content || !layout) return;
      mounts.disconnect();
      const update = () => {
        root.style.gridTemplateColumns = `${source.clientWidth}px minmax(0, 1fr)`;
        root.style.setProperty(CONTENT_WIDTH, `${content.clientWidth}px`);
        sidebar.style.opacity = getComputedStyle(content).opacity;
        sidebar.style.transform = getComputedStyle(source).transform;
        sidebar.style.pointerEvents = open ? 'auto' : 'none';
        // The route body is a separate stacking context, so its divider can't rise above this
        // layer. Leave the divider's left half uncovered; the slot below has the same sidebar
        // background.
        const divider = layout.querySelector<HTMLElement>(
          `:scope > .${launcherStyles.divider}`
        );
        if (!divider) {
          sidebar.style.removeProperty('clip-path');
          return;
        }
        const edge =
          divider.getBoundingClientRect().left -
          sidebar.getBoundingClientRect().left;
        sidebar.style.clipPath = `inset(0 calc(100% - ${Math.max(0, edge)}px) 0 0)`;
      };
      resize = new ResizeObserver(update);
      resize.observe(source);
      geometry = new MutationObserver(update);
      geometry.observe(content, {
        attributes: true,
        attributeFilter: ['style', 'data-open'],
      });
      update();
    };
    const mounts = new MutationObserver(attach);
    mounts.observe(viewport, { childList: true, subtree: true });
    attach();
    return () => {
      mounts.disconnect();
      resize?.disconnect();
      geometry?.disconnect();
      root.style.removeProperty('grid-template-columns');
      root.style.removeProperty(CONTENT_WIDTH);
      sidebar.style.removeProperty('opacity');
      sidebar.style.removeProperty('transform');
      sidebar.style.removeProperty('pointer-events');
      sidebar.style.removeProperty('clip-path');
    };
  }, [allPageActive, open]);

  useLayoutEffect(() => {
    const host = rootRef.current?.parentElement;
    const body = host ? workbenchBodyContent(host) : null;
    // A sidebar that stays is put away from the rail or by its own boundary only.
    if (!body || !open || placement !== 'sheet') return;
    // Observe the original gesture on this view's document, including Lit
    // descendants. Nothing intercepts it or synthesizes a second event.
    body.addEventListener('pointerdown', onDocumentInteract, {
      capture: true,
      passive: true,
    });
    return () => {
      body.removeEventListener('pointerdown', onDocumentInteract, true);
    };
  }, [open, onDocumentInteract, placement]);

  return (
    <div
      ref={layerRef}
      {...region.attributes}
      className={styles.layer}
      data-testid="workspace-document-navigation-layer"
      data-all-page={allPageActive}
      data-placement={placement}
      data-open={open}
      style={{ '--launcher-pane-width': `${width}px` } as CSSProperties}
    >
      <div
        ref={sidebarRef}
        className={styles.sidebar}
        data-open={beside ? undefined : allPageActive || open}
        data-collapsed={beside ? !open : undefined}
        inert={!open}
      >
        <DocumentsPaneSurface
          ref={surfaceRef}
          style={{
            width: `var(${CONTENT_WIDTH}, ${allPageActive ? `${width}px` : '100%'})`,
          }}
        >
          {children}
        </DocumentsPaneSurface>
      </div>
      {beside && (
        <LauncherSidebarDivider
          ref={dividerRef}
          className={styles.divider}
          open={open}
          resizable
          resizeLabel={t[
            'com.streamlain.rootAppSidebar.resize-handle.tooltip.drag'
          ]()}
          onResizeStart={startResize}
          onResetWidth={resetWidth}
          testId="workspace-document-navigation"
        />
      )}
    </div>
  );
}
